import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Loader2,
  FileText,
  Upload,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import {
  postCourseContentAPI,
  patchCourseContentAPI,
} from "../services/apicollection";
import FaqManager from "./FaqManager";

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4 rounded-lg border border-[#1d3350] bg-[#0f1c2e]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left font-medium text-slate-200"
      >
        {title}
        {open ? (
          <ChevronDown size={18} />
        ) : (
          <ChevronRight size={18} />
        )}
      </button>
      {open && <div className="border-t border-[#1d3350] p-4">{children}</div>}
    </div>
  );
};

const CourseContentForm = ({ initialData, onSuccess, onCancel }) => {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    course: "",
    description: "",
    topicOverview: [],
    content: [],
    projects: [],
    courseFeatures: [],
    corporateTraining: [],
    faq: [],
    courseFee: { totalFee: "", oldFee: "", registrationFee: "", emi: [] },
    syllabusFile: null,
    sampleTopics: [],
  });

  const isEditing = !!initialData?._id;

  useEffect(() => {
    async function load() {
      setLoadingCourses(true);
      try {
        const res = await axiosInstance.get(apiUrl.GETCOURSES);
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setCourses(res.data.data);
        }
      } catch (e) {
        setError("Failed to load courses");
      } finally {
        setLoadingCourses(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        course: initialData.course?._id || initialData.course || "",
        description: initialData.description || "",
        topicOverview: Array.isArray(initialData.topicOverview)
          ? initialData.topicOverview.map((t) => ({
              heading: t.heading || t.name || "",
              name: t.name || t.heading || "",
              topics: Array.isArray(t.topics) ? [...t.topics] : [],
            }))
          : [],
        content: Array.isArray(initialData.content)
          ? initialData.content.map((c) => ({ label: c.label || "", value: c.value || "" }))
          : [],
        projects: Array.isArray(initialData.projects) ? [...initialData.projects] : [],
        courseFeatures: Array.isArray(initialData.courseFeatures)
          ? [...initialData.courseFeatures]
          : [],
        corporateTraining: Array.isArray(initialData.corporateTraining)
          ? [...initialData.corporateTraining]
          : [],
        faq: Array.isArray(initialData.faq)
          ? initialData.faq.map((f) => ({
              category: f.category || "",
              questions: Array.isArray(f.questions)
                ? f.questions.map((q) => ({ que: q.que || "", ans: q.ans || "" }))
                : [],
            }))
          : [],
        courseFee: {
          totalFee: initialData.courseFee?.totalFee || "",
          oldFee: initialData.courseFee?.oldFee || "",
          registrationFee: initialData.courseFee?.registrationFee || "",
          emi: Array.isArray(initialData.courseFee?.emi)
            ? initialData.courseFee.emi.map((e) => ({
                installment: e.installment || "",
                amount: e.amount || "",
              }))
            : [],
        },
        syllabusFile: null,
        sampleTopics: Array.isArray(initialData.sampleTopics)
          ? initialData.sampleTopics.map((t) => ({
              topic: t.topic || "",
              videoUrl: t.videoUrl || "",
              notes: t.notes || "",
              imageUrl: t.imageUrl || "",
              codeSnippet: t.codeSnippet || "",
              codeLanguage: t.codeLanguage || "javascript",
              tryItYourself: !!t.tryItYourself,
            }))
          : [],
      });
    }
  }, [initialData]);

  const update = (path, value) => {
    setFormData((prev) => {
      const next = { ...prev };
      let cur = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        const idx = parseInt(k, 10);
        if (!Number.isNaN(idx)) {
          cur = cur[k] = [...(cur[k] || [])];
        } else {
          cur = cur[k] = { ...(cur[k] || {}) };
        }
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const addTopicOverview = () => {
    setFormData((p) => ({
      ...p,
      topicOverview: [...p.topicOverview, { heading: "", name: "", topics: [] }],
    }));
  };

  const addTopicToOverview = (idx, topic) => {
    setFormData((p) => {
      const arr = [...p.topicOverview];
      arr[idx] = { ...arr[idx], topics: [...(arr[idx].topics || []), topic] };
      return { ...p, topicOverview: arr };
    });
  };

  const addContentItem = () => {
    setFormData((p) => ({ ...p, content: [...p.content, { label: "", value: "" }] }));
  };

  const addProject = () => {
    setFormData((p) => ({ ...p, projects: [...p.projects, ""] }));
  };

  const addCourseFeature = () => {
    setFormData((p) => ({ ...p, courseFeatures: [...p.courseFeatures, ""] }));
  };

  const addCorporateTraining = () => {
    setFormData((p) => ({ ...p, corporateTraining: [...p.corporateTraining, ""] }));
  };

  const addEmi = () => {
    setFormData((p) => ({
      ...p,
      courseFee: {
        ...p.courseFee,
        emi: [...(p.courseFee.emi || []), { installment: "", amount: "" }],
      },
    }));
  };

  const addSampleTopic = () => {
    setFormData((p) => ({
      ...p,
      sampleTopics: [
        ...(p.sampleTopics || []),
        { topic: "", videoUrl: "", notes: "", imageUrl: "", codeSnippet: "", codeLanguage: "javascript", tryItYourself: false },
      ],
    }));
  };

  // Quick-fill 10 blank topic rows in one click, since the student panel
  // looks best with at least 10 placeholder topics until real lectures
  // are uploaded — saves clicking "Add Topic" ten times by hand.
  const addTenSampleTopics = () => {
    setFormData((p) => ({
      ...p,
      sampleTopics: [
        ...(p.sampleTopics || []),
        ...Array.from({ length: 10 }, () => ({ topic: "", videoUrl: "", notes: "", imageUrl: "", codeSnippet: "", codeLanguage: "javascript", tryItYourself: false })),
      ],
    }));
  };

  const updateSampleTopic = (idx, field, value) => {
    setFormData((p) => {
      const arr = [...p.sampleTopics];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...p, sampleTopics: arr };
    });
  };

  const removeArrayItem = (key, idx) => {
    setFormData((p) => {
      const arr = [...p[key]];
      arr.splice(idx, 1);
      return { ...p, [key]: arr };
    });
  };

  const removeTopicFromOverview = (idx, tIdx) => {
    setFormData((p) => {
      const arr = [...p.topicOverview];
      arr[idx].topics.splice(tIdx, 1);
      return { ...p, topicOverview: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        course: formData.course,
        description: formData.description,
        topicOverview: formData.topicOverview.map((t) => ({
          heading: t.heading || t.name,
          name: t.name || t.heading,
          topics: t.topics.filter(Boolean),
        })),
        content: formData.content.filter((c) => c.label || c.value),
        projects: formData.projects.filter(Boolean),
        courseFeatures: formData.courseFeatures.filter(Boolean),
        corporateTraining: formData.corporateTraining.filter(Boolean),
        faq: formData.faq
          .filter((f) => f.category || f.questions?.length)
          .map((f) => ({
            category: f.category,
            questions: (f.questions || []).filter((q) => q.que || q.ans),
          })),
        courseFee: {
          totalFee: formData.courseFee.totalFee,
          oldFee: formData.courseFee.oldFee,
          registrationFee: formData.courseFee.registrationFee,
          emi: formData.courseFee.emi.filter(
            (e) => e.installment || e.amount
          ),
        },
        syllabusFile: formData.syllabusFile,
        sampleTopics: (formData.sampleTopics || [])
          .filter((t) => t.topic || t.videoUrl || t.notes)
          .map((t) => ({
            topic: t.topic,
            videoUrl: t.videoUrl,
            notes: t.notes,
            imageUrl: t.imageUrl,
            codeSnippet: t.codeSnippet,
            codeLanguage: t.codeLanguage,
            tryItYourself: t.tryItYourself,
          })),
      };

      if (isEditing) {
        const res = await patchCourseContentAPI(initialData._id, payload);
        if (res?.status === false) throw new Error(res.message);
      } else {
        const res = await postCourseContentAPI(payload);
        if (res?.status === false) throw new Error(res.message);
      }
      onSuccess();
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCourses) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={28} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      <Section title="Course">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-300">
            Select Course
          </label>
          <select
            value={formData.course}
            onChange={(e) => setFormData((p) => ({ ...p, course: e.target.value }))}
            required
            // disabled={isEditing}
            className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
          >
            <option value="">-- Choose course --</option>
            {courses.map((c) => (
              <option key={c._id} value={c._id}>
                {c.courseName} ({c.category})
              </option>
            ))}
          </select>
        </div>
      </Section>

      <Section title="Description">
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData((p) => ({ ...p, description: e.target.value }))
          }
          rows={4}
          className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
          placeholder="Course overview..."
        />
      </Section>

      <Section title="Topic Overview (Syllabus Structure)">
        {formData.topicOverview.map((t, idx) => (
          <div key={idx} className="mb-4 rounded border border-[#1d3350] p-3">
            <div className="mb-2 flex gap-2">
              <input
                value={t.heading || t.name}
                onChange={(e) =>
                  setFormData((p) => {
                    const arr = [...p.topicOverview];
                    arr[idx] = {
                      ...arr[idx],
                      heading: e.target.value,
                      name: e.target.value,
                    };
                    return { ...p, topicOverview: arr };
                  })
                }
                placeholder="Module/Heading"
                className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={() => removeArrayItem("topicOverview", idx)}
                className="rounded p-1 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="space-y-1 pl-2">
              {(t.topics || []).map((topic, tIdx) => (
                <div key={tIdx} className="flex gap-2">
                  <input
                    value={topic}
                    onChange={(e) =>
                      setFormData((p) => {
                        const arr = p.topicOverview.map((item, i) =>
                          i === idx
                            ? {
                                ...item,
                                topics: (item.topics || []).map((tt, ti) =>
                                  ti === tIdx ? e.target.value : tt
                                ),
                              }
                            : item
                        );
                        return { ...p, topicOverview: arr };
                      })
                    }
                    placeholder="Topic"
                    className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeTopicFromOverview(idx, tIdx)}
                    className="text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addTopicToOverview(idx, "")}
                className="mt-1 text-sm text-slate-400 underline"
              >
                + Add topic
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addTopicOverview}
          className="inline-flex items-center gap-1 rounded bg-slate-700 px-3 py-1.5 text-sm text-white"
        >
          <Plus size={14} />
          Add module
        </button>
      </Section>

      <Section title="Course Content (Expandable sections)">
        {formData.content.map((c, idx) => (
          <div key={idx} className="mb-3 flex gap-2">
            <input
              value={c.label}
              onChange={(e) =>
                setFormData((p) => {
                  const arr = [...p.content];
                  arr[idx].label = e.target.value;
                  return { ...p, content: arr };
                })
              }
              placeholder="Label (e.g. What is X?)"
              className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <textarea
              value={c.value}
              onChange={(e) =>
                setFormData((p) => {
                  const arr = [...p.content];
                  arr[idx].value = e.target.value;
                  return { ...p, content: arr };
                })
              }
              placeholder="Content"
              rows={2}
              className="flex-[2] bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => removeArrayItem("content", idx)}
              className="text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addContentItem}
          className="inline-flex items-center gap-1 rounded bg-slate-700 px-3 py-1.5 text-sm text-white"
        >
          <Plus size={14} />
          Add content block
        </button>
      </Section>

      <Section title="Projects">
        {formData.projects.map((p, idx) => (
          <div key={idx} className="mb-2 flex gap-2">
            <input
              value={p}
              onChange={(e) =>
                setFormData((prev) => {
                  const arr = [...prev.projects];
                  arr[idx] = e.target.value;
                  return { ...prev, projects: arr };
                })
              }
              placeholder="Project name"
              className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => removeArrayItem("projects", idx)}
              className="text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addProject}
          className="inline-flex items-center gap-1 text-sm text-slate-400 underline"
        >
          <Plus size={14} />
          Add project
        </button>
      </Section>

      <Section title="Course Features">
        {formData.courseFeatures.map((f, idx) => (
          <div key={idx} className="mb-2 flex gap-2">
            <input
              value={f}
              onChange={(e) =>
                setFormData((p) => {
                  const arr = [...p.courseFeatures];
                  arr[idx] = e.target.value;
                  return { ...p, courseFeatures: arr };
                })
              }
              placeholder="Feature"
              className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => removeArrayItem("courseFeatures", idx)}
              className="text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addCourseFeature}
          className="inline-flex items-center gap-1 text-sm text-slate-400 underline"
        >
          <Plus size={14} />
          Add feature
        </button>
      </Section>

      <Section title="Corporate Training">
        {formData.corporateTraining.map((c, idx) => (
          <div key={idx} className="mb-2 flex gap-2">
            <input
              value={c}
              onChange={(e) =>
                setFormData((p) => {
                  const arr = [...p.corporateTraining];
                  arr[idx] = e.target.value;
                  return { ...p, corporateTraining: arr };
                })
              }
              placeholder="Benefit"
              className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => removeArrayItem("corporateTraining", idx)}
              className="text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addCorporateTraining}
          className="inline-flex items-center gap-1 text-sm text-slate-400 underline"
        >
          <Plus size={14} />
          Add item
        </button>
      </Section>

      <Section title="Registration Fee">
        <div className="space-y-3">
          <p className="text-xs text-slate-500 -mt-1">
            The course's total price and any discount come from the course's
            own "Price" field (set when creating/editing the course) and any
            active Offer — not here. This is only the booking/registration
            deposit amount, which is part of (not extra on top of) that
            total.
          </p>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-slate-500">
                Registration Fee (₹)
              </label>
              <input
                value={formData.courseFee.registrationFee}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    courseFee: {
                      ...p.courseFee,
                      registrationFee: e.target.value,
                    },
                  }))
                }
                placeholder="e.g. 3,000"
                className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm text-slate-400">
              EMI / Installments
            </label>
            {(formData.courseFee.emi || []).map((e, idx) => (
              <div key={idx} className="mb-2 flex gap-2">
                <input
                  value={e.installment}
                  onChange={(ev) =>
                    setFormData((p) => {
                      const arr = [...(p.courseFee.emi || [])];
                      arr[idx] = { ...arr[idx], installment: ev.target.value };
                      return {
                        ...p,
                        courseFee: { ...p.courseFee, emi: arr },
                      };
                    })
                  }
                  placeholder="e.g. 1st Installment"
                  className="flex-1 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
                />
                <input
                  value={e.amount}
                  onChange={(ev) =>
                    setFormData((p) => {
                      const arr = [...(p.courseFee.emi || [])];
                      arr[idx] = { ...arr[idx], amount: ev.target.value };
                      return {
                        ...p,
                        courseFee: { ...p.courseFee, emi: arr },
                      };
                    })
                  }
                  placeholder="Amount"
                  className="w-24 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={() =>
                    setFormData((p) => {
                      const arr = p.courseFee.emi.filter((_, i) => i !== idx);
                      return {
                        ...p,
                        courseFee: { ...p.courseFee, emi: arr },
                      };
                    })
                  }
                  className="text-red-400"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addEmi}
              className="text-sm text-slate-400 underline"
            >
              + Add installment
            </button>
          </div>
        </div>
      </Section>

      <Section title="Sample Topics — Placeholder Videos + Notes" defaultOpen={false}>
        <p className="text-xs text-slate-500 -mt-1 mb-3">
          Shown to students in the "Lectures" tab ONLY until the instructor
          uploads real recorded lectures for their batch. Aim for at least
          10 topics so the panel doesn't look empty on day one. Video links
          can be from YouTube (or any platform) — paste a normal share
          link, it'll be embedded automatically. Notes can be plain text or
          a link to a notes doc/PDF.
        </p>

        {(formData.sampleTopics || []).map((t, idx) => (
          <div key={idx} className="mb-3 rounded border border-[#1d3350] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400">
                Topic #{idx + 1}
              </span>
              <button
                type="button"
                onClick={() => removeArrayItem("sampleTopics", idx)}
                className="text-red-400"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <input
              value={t.topic}
              onChange={(e) => updateSampleTopic(idx, "topic", e.target.value)}
              placeholder="Topic title, e.g. 'Introduction to React Hooks'"
              className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <input
              value={t.videoUrl}
              onChange={(e) => updateSampleTopic(idx, "videoUrl", e.target.value)}
              placeholder="Video link, e.g. https://www.youtube.com/watch?v=..."
              className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <textarea
              value={t.notes}
              onChange={(e) => updateSampleTopic(idx, "notes", e.target.value)}
              placeholder="Notes (plain text, or a link to a notes PDF/doc)"
              rows={2}
              className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <input
              value={t.imageUrl}
              onChange={(e) => updateSampleTopic(idx, "imageUrl", e.target.value)}
              placeholder="Image URL (diagram/screenshot) — optional"
              className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm"
            />
            <textarea
              value={t.codeSnippet}
              onChange={(e) => updateSampleTopic(idx, "codeSnippet", e.target.value)}
              placeholder="Example code (shown in a code block) — optional"
              rows={4}
              className="w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded border border-[#1d3350] px-2 py-1 text-sm font-mono"
            />
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={t.codeLanguage}
                onChange={(e) => updateSampleTopic(idx, "codeLanguage", e.target.value)}
                className="bg-[#0d1220] text-slate-100 rounded border border-[#1d3350] px-2 py-1 text-sm"
              >
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="other">Other</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={!!t.tryItYourself}
                  onChange={(e) => updateSampleTopic(idx, "tryItYourself", e.target.checked)}
                />
                Show "Try Yourself" editor for this topic
                {t.codeLanguage !== "javascript" && (
                  <span className="text-amber-400">
                    (only JavaScript actually runs live — other languages
                    just get an editable code box)
                  </span>
                )}
              </label>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={addSampleTopic}
            className="text-sm text-slate-400 underline"
          >
            + Add one topic
          </button>
          <button
            type="button"
            onClick={addTenSampleTopics}
            className="text-sm text-cyan-400 underline"
          >
            + Add 10 topics at once
          </button>
        </div>
      </Section>

      <Section title="FAQ (course detail page par dikhenge)">
        {/* Naya FAQ system — add/edit/delete turant save hota hai,
            "Save" button dabane ki zaroorat nahi. Purana category-wise FAQ
            data (formData.faq) chhed nahi rahe, wo waise hi save hota rahega
            aur neeche se import kiya ja sakta hai. */}
        <FaqManager
          course={formData.course}
          legacyFaq={formData.faq}
          emptyHint="FAQ add karne ke liye pehle upar se course select karo."
        />
      </Section>

      <Section title="Course Syllabus (PDF)">
        <div className="flex items-center gap-4">
          <input
            type="file"
            accept=".pdf"
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                syllabusFile: e.target.files?.[0] || null,
              }))
            }
            className="text-sm"
          />
          {initialData?.syllabusPdf && !formData.syllabusFile && (
            <a
              href={initialData.syllabusPdf}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-sm text-cyan-400"
            >
              <FileText size={16} />
              Current syllabus
            </a>
          )}
        </div>
      </Section>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
          {submitting ? "Saving..." : isEditing ? "Update" : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CourseContentForm;